import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiCall } from "@/lib/api";
import type {
  ClienteCreateInput,
  ClienteDetail,
  ClienteUpdateInput,
  ContactoInput,
  ContactoOut,
  EstatusCliente,
  PagedClientes,
  ResumenOperativo,
  TipoCliente,
} from "./schemas";

const KEY = "clientes";

/** dev123 — columnas permitidas para sort. Debe matchear el allowlist
 * del backend (ver cliente_service._SORT_COLS). */
export type ClienteSortBy = "codigo" | "razon_social" | "rfc" | "tipo" | "estatus" | "fecha_alta";
export type SortDir = "asc" | "desc";

export type ClientesFilters = {
  page?: number;
  size?: number;
  search?: string;
  tipo?: TipoCliente | "";
  estatus?: EstatusCliente | "";
  sort_by?: ClienteSortBy;
  sort_dir?: SortDir;
};

function buildSearchParams(f: ClientesFilters): Record<string, string | number> {
  const params: Record<string, string | number> = {
    page: f.page ?? 1,
    size: f.size ?? 20,
  };
  if (f.search) params.search = f.search;
  if (f.tipo) params.tipo = f.tipo;
  if (f.estatus) params.estatus = f.estatus;
  if (f.sort_by) params.sort_by = f.sort_by;
  if (f.sort_dir) params.sort_dir = f.sort_dir;
  return params;
}

export function useListClientes(filters: ClientesFilters) {
  return useQuery({
    queryKey: [KEY, "list", filters],
    queryFn: () =>
      apiCall<PagedClientes>("get", "/api/v1/clientes", {
        searchParams: buildSearchParams(filters),
      }),
    placeholderData: (prev) => prev, // paginación suave
  });
}

export function useCliente(id: string | null) {
  return useQuery({
    queryKey: [KEY, "detail", id],
    queryFn: () => apiCall<ClienteDetail>("get", `/api/v1/clientes/${id}`),
    enabled: !!id,
  });
}

/** F14.4 — Resumen operativo: contratos agregados + facturas vencidas. */
export function useClienteResumen(id: string | null) {
  return useQuery({
    queryKey: [KEY, "resumen", id],
    queryFn: () =>
      apiCall<ResumenOperativo>("get", `/api/v1/clientes/${id}/resumen-operativo`),
    enabled: !!id,
    refetchOnWindowFocus: true,
  });
}

export function useCreateCliente() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ClienteCreateInput) =>
      apiCall<ClienteDetail>("post", "/api/v1/clientes", { json: data }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
    },
  });
}

export function useUpdateCliente(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ClienteUpdateInput) =>
      apiCall<ClienteDetail>("patch", `/api/v1/clientes/${id}`, { json: data }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
    },
  });
}

/**
 * @deprecated v1.5.0-rc2 — preferir el modal `DarDeBajaDialog` que llama
 * `GET /preview-baja` + `POST /dar-de-baja`. El DELETE actual ahora ejecuta
 * el flujo cascada y REQUIERE `motivo` como query param (mínimo 3 chars).
 * Esta función queda como fallback programático pero el UX correcto es
 * abrir el modal con preview.
 */
export function useDeleteCliente() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) =>
      apiCall<{ message: string }>(
        "delete",
        `/api/v1/clientes/${id}?motivo=${encodeURIComponent(motivo)}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
    },
  });
}

// ── Contactos (endpoints separados del cliente) ─────────────────────────────
// El PATCH /clientes/{id} NO toca contactos — hay que usar estos para agregar/
// editar/eliminar cada contacto individualmente.

export async function createContacto(
  clienteId: string,
  data: Omit<ContactoInput, "id">
): Promise<ContactoOut> {
  return apiCall<ContactoOut>("post", `/api/v1/clientes/${clienteId}/contactos`, { json: data });
}

export async function updateContacto(
  clienteId: string,
  contactoId: string,
  data: Partial<Omit<ContactoInput, "id">>
): Promise<ContactoOut> {
  return apiCall<ContactoOut>(
    "patch",
    `/api/v1/clientes/${clienteId}/contactos/${contactoId}`,
    { json: data }
  );
}

export async function deleteContacto(clienteId: string, contactoId: string): Promise<void> {
  await apiCall<{ message: string }>(
    "delete",
    `/api/v1/clientes/${clienteId}/contactos/${contactoId}`
  );
}

/**
 * Aplica el diff de contactos al backend:
 *   - Contactos en form sin `id` → POST (crea)
 *   - Contactos en form con `id` → PATCH (actualiza siempre, sin comparar campo por campo — es OK para el MVP)
 *   - Contactos originales cuyo `id` ya no aparece en form → DELETE
 */
export async function syncContactos(
  clienteId: string,
  originales: ContactoOut[],
  formContactos: ContactoInput[]
): Promise<void> {
  const formIds = new Set(formContactos.map((c) => c.id).filter(Boolean) as string[]);
  const eliminados = originales.filter((o) => !formIds.has(o.id));

  // 1) Eliminar los que ya no están
  await Promise.all(eliminados.map((o) => deleteContacto(clienteId, o.id)));

  // 2) Crear/actualizar los del form (en orden, respetando `es_principal`)
  for (const c of formContactos) {
    const { id, ...payload } = c;
    // Strip de strings vacíos a undefined para que Pydantic no los tome
    const clean: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(payload)) {
      if (typeof v === "string" && v.trim() === "") continue;
      clean[k] = v;
    }
    if (id) {
      await updateContacto(clienteId, id, clean);
    } else {
      await createContacto(clienteId, clean as Omit<ContactoInput, "id">);
    }
  }
}
